import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { api, NeuralChatApiError } from "../api/client";
import { useNeuralChatAuth } from "../contexts/AuthContext";
import { AgentExecutionResult, AgentJob, Message, Room } from "../types";
import { NeuralChatOutletContext } from "../components/NeuralChatShell";
import { MessageComposer } from "../components/MessageComposer";
import { CodexPilotPanel } from "../components/CodexPilotPanel";
import { MessageList } from "../components/MessageList";
import { AgentJobCard } from "../components/AgentJobCard";
import { Avatar, CenterState, ErrorNotice, JobCards, MobileMenu, OutlineButton, StateCard, Topbar, TopbarActions, TopbarCopy, TopbarLeft } from "../styles";
import { mergeMessages, shouldApplyRoomResponse, sortMessages } from "../utils/roomRequests";
import { RealtimeConnectionState, useNeuralChatRoomRealtime } from "../realtime/client";

export const NeuralChatRoomPage: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const auth = useNeuralChatAuth();
  const navigate = useNavigate();
  const { mentions, refreshMentions, refreshRooms } = useOutletContext<NeuralChatOutletContext>();
  const [room, setRoom] = useState<Room | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [jobs, setJobs] = useState<AgentJob[]>([]);
  const [executionResults, setExecutionResults] = useState<Record<string, AgentExecutionResult>>({});
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [error, setError] = useState("");
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  const [showNew, setShowNew] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const pollBusy = useRef(false);
  const firstLoad = useRef(true);
  const pollAbort = useRef<AbortController | null>(null);
  const olderAbort = useRef<AbortController | null>(null);
  const jobsAbort = useRef<AbortController | null>(null);
  const olderSequence = useRef(0);
  const roomGeneration = useRef(0);
  const currentRoomId = useRef(roomId);
  const messagesRef = useRef<Message[]>([]);
  currentRoomId.current = roomId;
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  const roomMentions = useMemo(() => mentions.filter((mention) => mention.room.id === roomId), [mentions, roomId]);

  const handleAuthFailure = useCallback((reason: unknown) => {
    if (reason instanceof NeuralChatApiError && reason.status === 401) { auth.markUnauthenticated(); navigate("/neuralchat/login", { replace: true }); return true; }
    return false;
  }, [auth, navigate]);

  const fetchRoom = useCallback(async (requestRoomId: string, requestGeneration: number, signal?: AbortSignal) => {
    const [nextRoom, page] = await Promise.all([api.getRoom(requestRoomId, signal), api.listMessages(requestRoomId, null, 50, signal)]);
    if (!shouldApplyRoomResponse({ requestRoomId, requestGeneration, currentRoomId: currentRoomId.current || "", currentGeneration: roomGeneration.current, aborted: signal?.aborted ?? false })) return;
    setRoom(nextRoom); setMessages(sortMessages(page.rows)); setCursor(page.nextCursor); setError("");
  }, []);

  useEffect(() => {
    if (!roomId) return;
    const generation = ++roomGeneration.current;
    olderSequence.current += 1;
    olderAbort.current?.abort();
    olderAbort.current = null;
    setRoom(null);
    setMessages([]);
    setJobs([]);
    setExecutionResults({});
    setCursor(null);
    setShowNew(false);
    setReplyTo(null);
    setLoadingOlder(false);
    const controller = new AbortController(); setLoading(true); firstLoad.current = true;
    void fetchRoom(roomId, generation, controller.signal).catch((reason) => {
      if (currentRoomId.current !== roomId || roomGeneration.current !== generation || controller.signal.aborted) return;
      if (!handleAuthFailure(reason) && !(reason instanceof DOMException && reason.name === "AbortError")) setError("채널 또는 메시지를 불러오지 못했습니다.");
    }).finally(() => {
      if (currentRoomId.current === roomId && roomGeneration.current === generation) setLoading(false);
    });
    jobsAbort.current?.abort();
    const jobsController = new AbortController();
    jobsAbort.current = jobsController;
    void api.listAgentJobs(roomId, jobsController.signal).then((nextJobs) => {
      if (currentRoomId.current === roomId && roomGeneration.current === generation && !jobsController.signal.aborted) setJobs(nextJobs);
    }).catch(() => undefined);
    return () => {
      controller.abort();
      jobsController.abort();
      olderAbort.current?.abort();
      olderAbort.current = null;
      olderSequence.current += 1;
    };
  }, [fetchRoom, handleAuthFailure, roomId]);

  useEffect(() => {
    if (!roomId || !jobs.length) return;
    const requestRoomId = roomId;
    const requestGeneration = roomGeneration.current;
    const controller = new AbortController();
    void Promise.all(jobs.map(async (job) => {
      try {
        const executions = await api.listAgentExecutions(job.id, controller.signal);
        const latest = executions.find((execution) => execution.status === "SUCCEEDED");
        if (!latest) return null;
        return [job.id, await api.getAgentExecutionResult(job.id, latest.id, controller.signal)] as const;
      } catch (reason) {
        if (reason instanceof DOMException && reason.name === "AbortError") throw reason;
        return null;
      }
    })).then((rows) => {
      if (controller.signal.aborted || currentRoomId.current !== requestRoomId || roomGeneration.current !== requestGeneration) return;
      setExecutionResults((current) => ({ ...current, ...Object.fromEntries(rows.filter((row): row is readonly [string, AgentExecutionResult] => row !== null)) }));
    }).catch(() => undefined);
    return () => controller.abort();
  }, [jobs, roomId]);

  const refreshJobs = useCallback(async () => {
    if (!roomId) return;
    const requestRoomId = roomId;
    const requestGeneration = roomGeneration.current;
    jobsAbort.current?.abort();
    const controller = new AbortController();
    jobsAbort.current = controller;
    try {
      const nextJobs = await api.listAgentJobs(requestRoomId, controller.signal);
      if (currentRoomId.current === requestRoomId && roomGeneration.current === requestGeneration && !controller.signal.aborted) setJobs(nextJobs);
    } catch (reason) {
      if (!(reason instanceof DOMException && reason.name === "AbortError")) return;
    }
  }, [roomId]);

  const isNearBottom = () => { const node = listRef.current; return !node || node.scrollHeight - node.scrollTop - node.clientHeight < 120; };
  const scrollBottom = (behavior: ScrollBehavior = "smooth") => { const node = listRef.current; if (node) node.scrollTo({ top: node.scrollHeight, behavior }); setShowNew(false); };
  const markLatestRead = useCallback((rows: Message[]) => {
    if (!roomId || document.hidden || !rows.length) return;
    const last = rows[rows.length - 1];
    void api.markRead(roomId, last.id).then(() => refreshMentions()).catch(() => undefined);
  }, [refreshMentions, roomId]);

  const poll = useCallback(async () => {
    if (!roomId || document.hidden || pollBusy.current) return;
    pollBusy.current = true;
    const requestRoomId = roomId;
    const requestGeneration = roomGeneration.current;
    const wasNear = isNearBottom();
    pollAbort.current?.abort();
    const controller = new AbortController();
    pollAbort.current = controller;
    try {
      const page = await api.listMessages(requestRoomId, null, 50, controller.signal);
      if (!shouldApplyRoomResponse({ requestRoomId, requestGeneration, currentRoomId: currentRoomId.current || "", currentGeneration: roomGeneration.current, aborted: controller.signal.aborted })) return;
      const knownIds = new Set(messagesRef.current.map((message) => message.id));
      const hasNewMessages = page.rows.some((message) => !knownIds.has(message.id));
      setMessages((current) => mergeMessages(current, page.rows));
      setCursor((current) => current || page.nextCursor);
      if (wasNear) requestAnimationFrame(() => {
        if (shouldApplyRoomResponse({ requestRoomId, requestGeneration, currentRoomId: currentRoomId.current || "", currentGeneration: roomGeneration.current, aborted: controller.signal.aborted })) {
          scrollBottom("auto");
          markLatestRead(sortMessages(page.rows));
        }
      }); else if (hasNewMessages) setShowNew(true);
    } catch (reason) {
      if (currentRoomId.current !== requestRoomId || roomGeneration.current !== requestGeneration || controller.signal.aborted) return;
      if (!handleAuthFailure(reason) && !(reason instanceof DOMException && reason.name === "AbortError")) setError("새 메시지를 확인하지 못했습니다.");
    }
    finally { pollBusy.current = false; }
  }, [handleAuthFailure, markLatestRead, roomId]);

  const realtimeState = useNeuralChatRoomRealtime(roomId, {
    onMessage: (event) => {
      if (event.room_id === roomId) void poll();
    },
    onAgentJob: (event) => {
      if (event.room_id === roomId) void refreshJobs();
    },
    onAuthorizationLost: (code) => {
      if (code === 4401) {
        auth.markUnauthenticated();
        navigate("/neuralchat/login", { replace: true });
      } else {
        void refreshRooms();
        navigate("/neuralchat", { replace: true });
      }
    },
  });

  useEffect(() => {
    const interval = realtimeState === "connected" ? 30000 : 5000;
    const timer = window.setInterval(() => { void poll(); }, interval);
    return () => { window.clearInterval(timer); pollAbort.current?.abort(); };
  }, [poll, realtimeState]);

  useEffect(() => {
    if (!loading && firstLoad.current && messages.length) {
      firstLoad.current = false;
      const requestRoomId = roomId;
      const requestGeneration = roomGeneration.current;
      requestAnimationFrame(() => {
        if (requestRoomId === currentRoomId.current && requestGeneration === roomGeneration.current) {
          scrollBottom("auto");
          markLatestRead(messages);
        }
      });
    }
  }, [loading, markLatestRead, messages, roomId]);

  useEffect(() => {
    const node = listRef.current; if (!node) return;
    const onScroll = () => { if (isNearBottom()) setShowNew(false); };
    node.addEventListener("scroll", onScroll); return () => node.removeEventListener("scroll", onScroll);
  }, [messages.length]);

  const loadOlder = async () => {
    if (!roomId || !cursor || loadingOlder || !listRef.current) return;
    const requestRoomId = roomId;
    const requestGeneration = roomGeneration.current;
    const requestSequence = olderSequence.current + 1;
    olderSequence.current = requestSequence;
    olderAbort.current?.abort();
    const controller = new AbortController();
    olderAbort.current = controller;
    const requestCursor = cursor;
    const node = listRef.current; const oldHeight = node.scrollHeight; const oldTop = node.scrollTop;
    setLoadingOlder(true);
    try {
      const page = await api.listMessages(requestRoomId, requestCursor, 50, controller.signal);
      if (!shouldApplyRoomResponse({ requestRoomId, requestGeneration, currentRoomId: currentRoomId.current || "", currentGeneration: roomGeneration.current, aborted: controller.signal.aborted }) || requestSequence !== olderSequence.current) return;
      setMessages((current) => mergeMessages(current, page.rows));
      setCursor(page.nextCursor);
      requestAnimationFrame(() => {
        if (shouldApplyRoomResponse({ requestRoomId, requestGeneration, currentRoomId: currentRoomId.current || "", currentGeneration: roomGeneration.current, aborted: controller.signal.aborted }) && requestSequence === olderSequence.current) {
          node.scrollTop = node.scrollHeight - oldHeight + oldTop;
        }
      });
    }
    catch (reason) {
      if (currentRoomId.current !== requestRoomId || roomGeneration.current !== requestGeneration || controller.signal.aborted || requestSequence !== olderSequence.current) return;
      if (!handleAuthFailure(reason)) setError("이전 메시지를 불러오지 못했습니다.");
    }
    finally {
      if (requestSequence === olderSequence.current) {
        olderAbort.current = null;
        setLoadingOlder(false);
      }
    }
  };

  useEffect(() => {
    if (!roomId || !messages.length || !isNearBottom() || document.hidden) return;
    const last = messages[messages.length - 1];
    const timer = window.setTimeout(() => { void api.markRead(roomId, last.id).then(() => refreshMentions()).catch(() => undefined); }, 350);
    return () => window.clearTimeout(timer);
  }, [messages, refreshMentions, roomId]);

  if (!roomId) return null;
  if (loading && !room) return <CenterState><StateCard><h1>채널을 여는 중…</h1></StateCard></CenterState>;
  if (!room) return <CenterState><StateCard><h1>채널을 찾을 수 없습니다</h1><p>{error || "접근 권한이 없거나 삭제된 채널입니다."}</p><OutlineButton type="button" onClick={() => navigate("/neuralchat")}>채널 목록으로</OutlineButton></StateCard></CenterState>;
  const canPost = room.members?.find((member) => member.id === auth.member?.id)?.can_post ?? true;
  const realtimeLabel: Record<RealtimeConnectionState, string> = {
    connecting: "실시간 연결 중",
    connected: "실시간 연결됨",
    reconnecting: "재연결 중",
    fallback: "polling fallback",
  };
  return <>
    <Topbar><TopbarLeft><MobileMenu type="button" aria-label="채널 목록 열기" onClick={() => window.dispatchEvent(new CustomEvent("neuralchat-open-sidebar"))}>☰</MobileMenu><TopbarCopy><h1>#{room.name}</h1><p>{room.description || "팀 대화"} · {room.members?.length || 0}명</p></TopbarCopy></TopbarLeft><TopbarActions><span aria-live="polite" style={{ color: realtimeState === "connected" ? "#15803d" : "#b45309", fontSize: 11, fontWeight: 800 }}>{realtimeLabel[realtimeState]}</span><OutlineButton type="button" onClick={() => window.dispatchEvent(new CustomEvent("neuralchat-open-mentions"))}>{roomMentions.length ? `멘션 ${roomMentions.length}` : "멘션"}</OutlineButton><Avatar $kind={auth.member?.kind} $small>{auth.member?.display_name.slice(0, 1)}</Avatar></TopbarActions></Topbar>
    {error && <ErrorNotice role="alert">{error}</ErrorNotice>}
    {auth.member?.kind === "HUMAN" && auth.member.role === "CEO" && <CodexPilotPanel roomId={roomId} onStarted={() => void refreshJobs()} />}
    {jobs.length > 0 && <JobCards>{jobs.map((job) => <AgentJobCard key={job.id} job={job} result={executionResults[job.id]} />)}</JobCards>}
    <MessageList messages={messages} loading={loading} loadingOlder={loadingOlder} hasOlder={Boolean(cursor)} onLoadOlder={loadOlder} onReply={setReplyTo} onJumpToBottom={() => scrollBottom()} showNewMessage={showNew} containerRef={listRef} />
    <MessageComposer roomId={roomId} roomGeneration={roomGeneration.current} members={room.members || []} archived={!canPost || room.is_archived} replyTo={replyTo} onCancelReply={() => setReplyTo(null)} onSent={(message, requestRoomId, requestGeneration) => {
      if (!shouldApplyRoomResponse({ requestRoomId, requestGeneration, currentRoomId: currentRoomId.current || "", currentGeneration: roomGeneration.current, aborted: false })) return;
      setMessages((current) => mergeMessages(current, [message]));
      requestAnimationFrame(() => {
        if (requestRoomId === currentRoomId.current && requestGeneration === roomGeneration.current) {
          scrollBottom();
          markLatestRead([message]);
        }
      });
    }} onTaskRequestCreated={refreshJobs} />
  </>;
};
